import { useMemo } from 'react';

import { TypedPolicy } from '../../../types';

import {
  DockerSecurityFormData,
  DockerSecurityPolicyData,
  DOCKER_SECURITY_INITIAL_VALUES,
} from './types';

/**
 * Type guard to check if data is DockerSecurityPolicyData
 */
function isDockerSecurityPolicyData(
  data: unknown
): data is DockerSecurityPolicyData {
  return (
    typeof data === 'object' &&
    data !== null &&
    'allowBindMountsForRegularUsers' in data
  );
}

/**
 * Hook to get initial values for Docker Security Policy form
 *
 * @param policy - Optional policy to load values from (for edit mode)
 * @returns Form initial values
 */
export function useDockerSecurityInitialValues(
  policy?: TypedPolicy<unknown>
): DockerSecurityFormData {
  return useMemo(() => {
    // Create mode: use defaults
    if (!policy) {
      return DOCKER_SECURITY_INITIAL_VALUES;
    }

    // Edit mode: deserialize from policy data
    const data = policy.Data;
    if (!isDockerSecurityPolicyData(data)) {
      return DOCKER_SECURITY_INITIAL_VALUES;
    }

    return {
      allowBindMountsForRegularUsers:
        data.allowBindMountsForRegularUsers?.value ?? true,
      allowPrivilegedModeForRegularUsers:
        data.allowPrivilegedModeForRegularUsers?.value ?? true,
      allowHostNamespaceForRegularUsers:
        data.allowHostNamespaceForRegularUsers?.value ?? true,
      allowDeviceMappingForRegularUsers:
        data.allowDeviceMappingForRegularUsers?.value ?? true,
      allowStackManagementForRegularUsers:
        data.allowStackManagementForRegularUsers?.value ?? true,
      allowContainerCapabilitiesForRegularUsers:
        data.allowContainerCapabilitiesForRegularUsers?.value ?? true,
      allowSysctlSettingForRegularUsers:
        data.allowSysctlSettingForRegularUsers?.value ?? true,
    };
  }, [policy]);
}
